import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../../Admin/shared';
import { useUserFriends } from '../api/useUserFriends';
import type { Friendship } from '../api/types';
import { DateRangeControl } from '@/components/shared';

function daysAgoInput(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

/** `—` for a value we could not compute. Never `0` — that is a different claim. */
function fmtNum(v: number | null): string {
  return v === null ? '—' : v.toLocaleString();
}

/** `null` means no friendship has ever formed — an em-dash, never a bogus date. */
function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** `display_name` and `email` can both be null — fall back to the id, never blank or "null". */
function personLabel(person: { display_name: string | null; email?: string | null }, id: string): string {
  return person.display_name ?? person.email ?? id;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight">{value}</p>
      {hint && <p className="mt-1.5 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

/**
 * Who asked, spelled out rather than the raw `'user' | 'friend' | null` enum.
 * `null` means no accepted friend-request row matched the pair — direction is
 * genuinely unknown, and must never be guessed at or attributed to either side.
 */
function WhoAsked({
  friendship,
  viewedUserLabel,
}: {
  friendship: Friendship;
  viewedUserLabel: string;
}): React.ReactElement {
  if (friendship.initiated_by === null) {
    return (
      <span className="text-gray-600" title="No request record — direction is unknown">
        —
      </span>
    );
  }
  const askedLabel =
    friendship.initiated_by === 'user'
      ? viewedUserLabel
      : personLabel(friendship, friendship.friend_user_id);
  return <span className="text-gray-300">{askedLabel}</span>;
}

/**
 * Per-user drill-down: this user's four headline stats for the selected
 * window, then the underlying friendship list — when each friendship formed,
 * who the friend was, and (when known) who sent the request.
 */
export function UserFriendsPage(): React.ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const [from, setFrom] = useState<string | null>(daysAgoInput(29));
  const [to, setTo] = useState<string | null>(todayInput());

  const { data, loading, error } = useUserFriends(userId ?? '', {
    from: from ?? undefined,
    to: to ?? undefined,
  });

  const viewedUserLabel = data ? personLabel(data.user, data.user.user_id) : (userId ?? '');

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-5">
        <div>
          <Link to="/analytics/friends" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
            ← Back to friend analytics
          </Link>
        </div>

        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-white tracking-tight">{viewedUserLabel}</h1>
            {data?.user.email && data.user.display_name && (
              <p className="mt-1 text-xs text-gray-500">{data.user.email}</p>
            )}
            {!data && !loading && <p className="mt-1 text-xs text-gray-500">User</p>}
          </div>
          <DateRangeControl
            from={from}
            to={to}
            onChange={(f, t) => {
              setFrom(f);
              setTo(t);
            }}
          />
        </header>

        {error && <ErrorBanner message={error} />}
        {loading && !data && (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat
                label="Friends"
                value={fmtNum(data.stats.friends_lifetime)}
                hint="current total — ignores the date range"
              />
              <Stat
                label="Added in period"
                value={fmtNum(data.stats.friends_in_range)}
                hint="formed inside the selected range"
              />
              <Stat label="Initiated" value={fmtNum(data.stats.friends_initiated)} hint="requests they sent" />
              <Stat label="Received" value={fmtNum(data.stats.friends_received)} hint="requests they accepted" />
            </div>

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Friendships</h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  Every friendship on record for this user, most recent first.
                </p>
              </div>

              {data.friendships_truncated && (
                <div className="mx-5 mt-4 flex items-start gap-2 p-3 bg-amber-950/40 border border-amber-800/50 rounded-xl">
                  <svg
                    className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m0 3.75h.008M10.29 3.86l-8.18 14.16A1.5 1.5 0 003.42 20.5h17.16a1.5 1.5 0 001.31-2.48L13.71 3.86a1.5 1.5 0 00-2.42 0z"
                    />
                  </svg>
                  <p className="text-sm text-amber-300">
                    Only the most recent 500 friendships are listed for this user.
                  </p>
                </div>
              )}

              {data.friendships.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-gray-500">
                  This user has no friends on record.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          When
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Who
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Who asked
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.friendships.map((f) => (
                        <tr
                          key={f.friend_user_id}
                          className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                        >
                          <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{fmtDate(f.friends_since)}</td>
                          <td className="px-5 py-3">
                            <Link
                              to={`/analytics/friends/${f.friend_user_id}`}
                              className="text-gray-200 hover:text-violet-300 transition-colors font-medium"
                            >
                              {personLabel(f, f.friend_user_id)}
                            </Link>
                          </td>
                          <td className="px-5 py-3">
                            <WhoAsked friendship={f} viewedUserLabel={viewedUserLabel} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default UserFriendsPage;
